// ABOUTME: The chat card "Confirm your brand kit": after the website import proposes a logo,
// colours and fonts, the owner confirms them or edits them first. Colours and fonts then
// apply to generated images by default. Shown only while the backend says confirmation is due;
// confirming reloads the thread so the "Brand kit saved" message the backend wrote shows at once.
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  BRAND_KIT_CONFIRMATION_KEY,
  confirmBrandKit,
  useBrandKitConfirmation,
  type BrandKitEdits,
  type BrandKitSummary,
} from '@/api/brandKitConfirm'
import { CHAT_CARD_BLOCK } from '@/components/chat/chatCard'
import { loadHermesChatHistory } from '@/components/chat/hermesHistory'
import { useAIChatStore } from '@/stores/useAIChatStore'

const COLORS = [
  ['primary_color', 'Primary'],
  ['secondary_color', 'Secondary'],
  ['accent_color', 'Accent'],
] as const
const FONTS = [
  ['heading_font', 'Headings'],
  ['body_font', 'Body'],
] as const

type EditKey = (typeof COLORS)[number][0] | (typeof FONTS)[number][0]

function Swatch({ color }: { color: string }) {
  return (
    <span
      className="border-border inline-block size-6 shrink-0 rounded-full border"
      style={{ backgroundColor: color }}
      aria-hidden
    />
  )
}

function KitView({ kit }: { kit: BrandKitSummary }) {
  return (
    <div className="space-y-3">
      {kit.logo_url && (
        <img src={kit.logo_url} alt="Your logo" className="h-12 max-w-40 object-contain" />
      )}
      <ul className="flex flex-wrap gap-x-4 gap-y-2">
        {COLORS.map(([key, label]) =>
          kit[key] ? (
            <li key={key} className="flex items-center gap-2 text-xs">
              <Swatch color={kit[key]} />
              <span>
                {label} <span className="text-muted-foreground">{kit[key]}</span>
              </span>
            </li>
          ) : null
        )}
      </ul>
      <p className="text-muted-foreground text-xs">
        {[
          kit.heading_font && `${kit.heading_font} headings`,
          kit.body_font && `${kit.body_font} body`,
        ]
          .filter(Boolean)
          .join(' · ') || 'No fonts found'}
      </p>
    </div>
  )
}

function KitEditor({
  kit,
  edits,
  onChange,
}: {
  kit: BrandKitSummary
  edits: BrandKitEdits
  onChange: (key: EditKey, value: string) => void
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {[...COLORS, ...FONTS].map(([key, label]) => (
        <label key={key} className="space-y-1 text-xs font-medium">
          {label}
          <Input
            value={edits[key] ?? kit[key] ?? ''}
            onChange={(e) => onChange(key, e.target.value)}
            placeholder={key.endsWith('color') ? '#1a2b3c' : 'Font family'}
            aria-label={`${label} ${key.endsWith('color') ? 'colour' : 'font'}`}
          />
        </label>
      ))}
    </div>
  )
}

export function BrandKitConfirmCard() {
  const { data } = useBrandKitConfirmation()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [edits, setEdits] = useState<BrandKitEdits>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!data?.needs_confirmation || !data.kit) return null
  const kit = data.kit

  const confirm = async () => {
    setSaving(true)
    setError(null)
    try {
      await confirmBrandKit(kit.id, edits)
      const conversationId = useAIChatStore.getState().activeConversationId
      if (conversationId) {
        const restored = await loadHermesChatHistory()
        useAIChatStore.getState().restoreMessages(conversationId, restored)
      }
      await queryClient.invalidateQueries({ queryKey: BRAND_KIT_CONFIRMATION_KEY })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className={CHAT_CARD_BLOCK} data-testid="brand-kit-confirm-card">
      <h3 className="text-sm font-semibold">Confirm your brand kit</h3>
      <p className="text-muted-foreground text-xs">
        This is what I found on your website. Your colours and fonts will be used on the images I
        make.
      </p>
      {editing ? (
        <KitEditor
          kit={kit}
          edits={edits}
          onChange={(key, value) => setEdits((prev) => ({ ...prev, [key]: value }))}
        />
      ) : (
        <KitView kit={kit} />
      )}
      {error && <p className="text-destructive text-xs">{error}</p>}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={confirm} disabled={saving}>
          {editing ? 'Save and confirm' : 'Confirm'}
        </Button>
        {!editing && (
          <Button size="sm" variant="outline" onClick={() => setEditing(true)} disabled={saving}>
            Edit
          </Button>
        )}
      </div>
    </div>
  )
}
